<template>
  <template v-if="$route.name === 'etachanges'">
    <a class="mds-text--small-normal" href="/bookings"
      ><mc-icon icon="arrow-left" />{{ $t("manageBookings.backText") }}
    </a>
    <header class="header header--eta">
      <div class="header__title">
        <h2 data-test="header-eta">{{ $t("tab.etaChanges") }}</h2>
      </div>
      <SearchBar class="header__search" data-test="search-eta" />
    </header>
  </template>

  <!-- Loading -->
  <mc-loading-indicator
    v-if="showSpinner"
    class="card card--loading"
    variant="spinner"
    appearance="primary"
    fit="large"
    :label="$t('common.loading')"
  />

  <div v-else data-test="eta-changes" class="eta-changes">
    <div class="mb-4" data-test="description-eta">{{ $t("eta.description") }}</div>

    <!-- Filters -->
    <div data-test="eta-changes-tab-filters" class="table-filters">
      <div class="table-filters__wrapper">
        <mc-select
          :label="$t('filter.sortBy')"
          name="sortBy"
          :value="sortByValue"
          labelposition="left"
          @optionselected="updateSortBy"
        >
          <mc-option v-for="opt in sortByOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</mc-option>
        </mc-select>
        <mc-select
          :label="$t('filter.etaChangedBy')"
          name="etaChangedBy"
          labelposition="left"
          :value="etaChangedByValue"
          @optionselected="updateETAChange"
        >
          <mc-option v-for="opt in etaChangedByOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</mc-option>
        </mc-select>
      </div>
      <JsonToCSV
        :filterValue="etaChangedByValue"
        view-name="ETA_Changes"
        :bookings="shipments"
        :pageNumber="paginationToken"
      />
    </div>

    <!-- No results -->
    <MarketingNotification v-if="shipments && shipments.length === 0 && !shipmentsError && !showSpinner" class="card" />

    <!-- Error -->
    <mc-notification
      v-else-if="shipments && shipments.length === 0 && !showSpinner"
      appearance="error"
      icon="exclamation-circle"
      width="fit-content"
      class="card"
      :body="$t('datatable.error.noRecords500')"
    />

    <!-- Results -->
    <mc-table
      v-else
      fit="small"
      expand
      sortdisabled
      :data.prop="shipments"
      :columns.prop="[
        {
          id: 'blNumber',
          label: $t('common.blNumber'),
          subDataLabel: $t('tables.references'),
          verticalAlign: 'top',
          width: '175px'
        },
        {
          id: 'from',
          label: $t('common.from'),
          subDataLabel: $t('tables.depPort'),
          verticalAlign: 'top'
        },
        {
          id: 'to',
          label: $t('common.to'),
          subDataLabel: $t('tables.arrPort'),
          verticalAlign: 'top'
        },
        {
          id: 'cargo',
          label: $t('common.cargo'),
          verticalAlign: 'top',
          width: '175px'
        },
        {
          id: 'originalETA',
          label: $t('eta.original'),
          verticalAlign: 'top',
          width: '100px'
        },
        {
          id: 'newETA',
          label: $t('eta.new'),
          verticalAlign: 'top'
        }
      ]"
      data-test="eta-changes-table"
    >
      <template v-for="item in shipments" :key="item.id">
        <div :slot="`${item.id}_blNumber`">
          <a
            data-test="document-table-shipment-link"
            :href="`/shipment-details/${item.ladingNumber}`"
            @click="trackShipmentClick('etachanges', $route?.path?.toLowerCase()?.includes?.('export'))"
          >
            <b>{{ item.ladingNumber }}</b>
          </a>
          <p v-if="item.bookedByRef" class="color-neutral-subtle mds-text--small-normal wrap-anywhere">
            {{ $t("common.bookedByRef") }},
            {{ item.bookedByRef }}
          </p>
        </div>
        <div :slot="`${item.id}_from`">
          <FromTo :place="item?.placeOfReceipt || item?.portOfLoading" :port="item?.portOfLoading" />
        </div>
        <div :slot="`${item.id}_to`">
          <FromTo
            :place="item?.placeOfDelivery || item?.portOfDischarge"
            :port="item?.portOfDischarge"
            type="arrival"
          />
        </div>
        <div :slot="`${item.id}_cargo`">
          <Cargo
            :commodity="item?.cargo?.commodity"
            :packageQuantity="item?.cargo?.containerQuantity"
            :packageQuantityUnit="item?.cargo?.containerName"
          />
        </div>
        <div :slot="`${item.id}_originalETA`">
          {{
            item?.portOfDischarge?.arrival?.originalArrival
              ? dayjs(item?.portOfDischarge?.arrival?.originalArrival).format("DD MMM HH:mm")
              : ""
          }}
        </div>
        <div :slot="`${item.id}_newETA`">
          {{
            item?.portOfDischarge?.arrival?.dateTime
              ? dayjs(item?.portOfDischarge?.arrival?.dateTime).format("DD MMM HH:mm")
              : ""
          }}
          <div data-test="ETAUpdated-Content">
            <mc-text-and-icon>
              <span slot="icon">
                <mc-icon color="#767676" icon="arrow-clockwise-clock" />
              </span>
              <p class="color-neutral-subtle mds-text--small-normal">
                {{ $t("eta.updated") }}
                {{ dayjs(item.etaLastUpdated).format("DD MMM") }}
              </p>
            </mc-text-and-icon>
          </div>
        </div>
        <div :slot="`${item.id}_expanded`">
          <mc-notification :body="item.etaChangeReason" appearance="warning" icon="info-circle" />
        </div>
      </template>
    </mc-table>

    <Pagination
      v-if="shipments && shipments.length > 0 && lastPage > 1"
      activeView="etaChanges"
      :lastPage="lastPage"
      :totalCount="totalCount"
      :currentPage="paginationToken"
      @update-data-source="getData"
    />
  </div>
</template>
<script>
import Pagination from "@/components/shared/Pagination.vue";
import { mapActions, mapState, mapWritableState } from "pinia";
import { etaChanges } from "@/store/etaChanges";
import { filters } from "@/store/filters";
import MarketingNotification from "@/components/shared/MarketingNotification.vue";
import { getExportImportPage, trackShipmentClick } from "@/lib/utilities";
import JsonToCSV from "@/components/shared/JsonToCSV.vue";
import FromTo from "@/components/shared/FromTo.vue";
import Cargo from "@/components/shared/Cargo.vue";
import "@maersk-global/mds-components-core/mc-table";
import dayjs from "dayjs";
import SearchBar from "@/components/search/SearchBar.vue";

export default {
  name: "ETAChanges",
  components: { JsonToCSV, MarketingNotification, Pagination, FromTo, Cargo, SearchBar },
  data() {
    return {
      sortByOptions: [
        { label: this.$t("filter.dropdown.lastUpdated"), value: "latest_updated_eta" },
        { label: this.$t("eta.new"), value: "new_eta" }
      ],
      etaChangedByOptions: [
        { label: this.$t("filter.dropdown.all"), value: "all" },
        { label: this.$t("filter.dropdown.1to3days"), value: "1_to_3_days" },
        { label: this.$t("filter.dropdown.3to7days"), value: "3_to_7_days" },
        { label: this.$t("filter.dropdown.over7days"), value: "over_7_days" }
      ],
      getExportImportPage
    };
  },
  computed: {
    ...mapState(etaChanges, [
      "lastPage",
      "shipments",
      "shipmentsError",
      "totalCount",
      "showSpinner",
      "etaChangedByValue",
      "sortByValue",
      "paginationToken"
    ]),
    ...mapWritableState(etaChanges, ["sortByValue", "etaChangedByValue"]),
    ...mapState(filters, ["activeFilter"]),
    departingOrArriving() {
      if (getExportImportPage() === "EXP") {
        return `${this.$t("common.departingIn")}:`;
      }

      if (getExportImportPage() === "IMP") {
        return `${this.$t("common.arrivingIn")}:`;
      }

      return "N/A:";
    }
  },
  watch: {
    etaChangedByValue: {
      handler: async function (newValue, oldValue) {
        if (newValue !== oldValue) {
          await this.updatePaginationToken(1);
          await this.getBookings();
          this.$sendGAEvent({
            event: "DQCS",
            action: "Click",
            category: "SO_QuickFilter",
            label: {
              data: "ETA changes quick filter",
              view: `${getExportImportPage()}`,
              departingOrArriving: this.departingOrArriving,
              timeframe: this.etaChangedByValue.replaceAll("_", " ")
            }
          });
        }
      },
      deep: true
    },
    sortByValue: {
      handler: async function (newValue, oldValue) {
        if (newValue !== oldValue) {
          await this.updatePaginationToken(1);
          await this.getBookings();
          this.$sendGAEvent({
            event: "DQCS",
            action: "Click",
            category: "SO_QuickFilter",
            label: {
              data: "ETA changes sort by filter",
              view: `${getExportImportPage()}`,
              departingOrArriving: this.departingOrArriving,
              timeframe: this.sortByValue.replaceAll("_", " ")
            }
          });
        }
      },
      deep: true
    },
    activeFilter: {
      async handler() {
        await this.updatePaginationToken(1);
        await this.getBookings();
      },
      deep: true
    }
  },
  async mounted() {
    this.setDefaultSortValues();
    if (this.shipments && this.shipments.length === 0) {
      await this.getBookings();
    }

    this.$sendGAEvent({
      event: "DQCS",
      action: "View",
      category: "Component",
      label: {
        data: "c1v_etachanges"
      }
    });
  },
  methods: {
    ...mapActions(etaChanges, [
      "getBookings",
      "updateSortBy",
      "updateETAChange",
      "updatePaginationToken",
      "setDefaultSortValues"
    ]),
    trackShipmentClick,
    dayjs,
    async getData(page) {
      if (page === this.paginationToken) return;
      await this.updatePaginationToken(page);
      await this.getBookings();
    }
  }
};
</script>
